// import { Cart } from '@/@types/shopify';
import { MiniCart } from '@/entrypoints/mini-cart/elements/mini-cart';
import { CartEvents, CartLine, TypedCartEvent, Cart } from '@/entrypoints/mini-cart/internals/core';

type RewardTier = {
  title: string;
  subtitle: {
    desktop: string;
    mobile: string;
  };
  priceThreshold: number;
  encouragementMessage: string;
};

export default class CartRewardBanner {
  private rewardTiers: RewardTier[];
  private rootElement: HTMLElement | null = null;
  private progressWrapper: HTMLElement | null = null;
  private progressThumb: HTMLElement | null = null;
  private encouragementMessageElem: HTMLElement | null = null;
  private highestUnlockedTier = 0;
  private cartTotal: number;
  private rewardTierProgress: number[] = [0, 25, 55, 75, 100];
  private numberFormatter = new Intl.NumberFormat('en-AU', {
    minimumFractionDigits: 2,
  });

  private blacklistedProperties = ['__free_gift'];

  private verbose: boolean = false;

  constructor({
    rewardTiers,
    cartTotal,
    rootElement,
    verbose = false,
    blacklistedProperties = [],
  }: {
    rewardTiers: RewardTier[];
    cartTotal: number;
    rootElement: HTMLDivElement;
    verbose?: boolean;
    blacklistedProperties?: string[];
  }) {
    if (!rewardTiers) {
      throw new Error('No reward tiers provided');
    }

    if (typeof cartTotal !== 'number') {
      throw new TypeError(`cartTotal must be a number and be represented in dollars`);
    }

    if (typeof blacklistedProperties !== 'object' || !Array.isArray(blacklistedProperties))
      blacklistedProperties = [];

    if (!rootElement) {
      throw new Error('No rootElement provided');
    }

    this.cartTotal = cartTotal || 0;
    this.rewardTiers = rewardTiers;
    this.cartTotal = cartTotal;
    this.blacklistedProperties = blacklistedProperties;
    this.verbose = verbose;
    this.highestUnlockedTier = this.calculateHighestUnlockedTier();

    this.processRootElement(rootElement);
    this.bindListeners();
  }

  private processRootElement(rootElement: HTMLElement) {
    this.rootElement = rootElement;
    this.progressWrapper = this.rootElement.querySelector('[data-progress-wrapper]');
    this.progressThumb = this.rootElement.querySelector('[data-progress-thumb]') as HTMLDivElement;

    if (!this.progressThumb) throw new Error('No progress thumb found');

    this.encouragementMessageElem = this.rootElement.querySelector(
      '[data-encouragement-message]',
    ) as HTMLSpanElement;

    if (!this.encouragementMessageElem) throw new Error('No encouragement message element found');

    this.rootElement.querySelectorAll<HTMLDivElement>('[data-tier]').forEach((tier, index) => {
      const priceThreshold = this.rewardTiers[index].priceThreshold;
      const currencyRate = parseFloat(window.Shopify.currency.rate || '1');
      const thresholdAmount = priceThreshold * (currencyRate);
      const currencySymbol = tier.getAttribute('data-currency-symbol') || '$';
      const thresholdDisplayAmount = `${currencySymbol}${thresholdAmount.toFixed(0)}`;

      tier.querySelectorAll('.cart-reward-banner__reward-subtitle').forEach((subtitle) => {
        subtitle.textContent = subtitle.textContent?.replace('{{ threshold_money }}', thresholdDisplayAmount);
      });
    });
  }

  private bindListeners() {
    const miniCartElement = document.querySelector<MiniCart>('mini-cart');

    miniCartElement?.bus.on(
      CartEvents.DebouncedChange,
      (e: TypedCartEvent<'cart:changed:debounced'>) => {
        let cart: Cart = e.detail.cart;

        this.cartTotal = cart.items.reduce((cartTotal: number, item: CartLine) => {
          let skipProcessing = this.blacklistedProperties.some((prop) => {
            return item.properties.hasOwnProperty(prop);
          });

          if (skipProcessing) return cartTotal;

          return cartTotal + item.final_line_price / 100;
        }, 0);

        if (this.verbose) console.debug('Cart total updated to', this.cartTotal);

        this.update();
      },
    );

    // document.addEventListener('on:cart:update', (e: Event | CustomEvent) => {
    //   let cart: Cart = (e as CustomEvent).detail.cart;

    //   this.cartTotal = cart.items.reduce((cartTotal, item) => {
    //     let skipProcessing = this.blacklistedProperties.some((prop) => {
    //       return item.properties.hasOwnProperty(prop);
    //     });

    //     if (skipProcessing) return cartTotal;

    //     return cartTotal + item.final_line_price / 100;
    //   }, 0);

    //   if (this.verbose) console.debug('Cart total updated to', this.cartTotal);

    //   this.update();
    // });
  }

  public update() {
    requestIdleCallback(() => {
      this.highestUnlockedTier = this.calculateHighestUnlockedTier();
      this.updateTierIcons();
      this.updateEncouragementMessage();
      this.progressThumb!.style.width = `${this.getProgressPercentage()}%`;
    });
  }

  private getProgressPercentage(): number {
    return this.rewardTierProgress[this.highestUnlockedTier ?? 0];
  }

  private clamp(value: number, min: number, max: number) {
    return Math.max(min, Math.min(max, value));
  }

  private calculateHighestUnlockedTier(): number {
    let highestUnlockedTier = 0;
    for (let i = 0; i < this.rewardTiers.length; ++i) {
      if (this.cartTotal >= this.rewardTiers[i].priceThreshold) {
        highestUnlockedTier = i + 1;
      }
    }

    return highestUnlockedTier;
  }

  public updateTierIcons(): void {
    let tierWrappers = document.querySelectorAll<HTMLDivElement>('[data-tier]');

    if (!tierWrappers.length) {
      throw new Error('No tiers found');
    }

    for (let i = 0; i < tierWrappers.length; ++i) {
      const tierWrapper: HTMLDivElement | null = tierWrappers[i];

      if (!tierWrapper) continue;

      tierWrapper.dataset.tierUnlocked =
        this.highestUnlockedTier >= Number(tierWrapper.dataset.tier) ? 'true' : 'false';
    }
  }

  public updateEncouragementMessage(): void {
    const currencySymbol = document.querySelector<HTMLDivElement>('.cart-reward-banner__reward-tier[data-currency-symbol]')!.getAttribute('data-currency-symbol') || '$';
    
    this.encouragementMessageElem!.textContent = this.rewardTiers[
      this.clamp(this.highestUnlockedTier - 1, 0, this.rewardTiers.length)
    ].encouragementMessage.replace(/{remaining_amount}/gi, this.getRemainingAmount()).replace(/{currency_symbol}/gi, currencySymbol);
  }

  private roundToDecimalPlaces(value: number, decimalPlaces: number) {
    const multiplier = 10 ** decimalPlaces;
    return Math.round(value * multiplier) / multiplier;
  }

  private getRemainingAmount(): string {
    if (this.highestUnlockedTier === this.rewardTiers.length) {
      return '0.00';
    }

    return this.numberFormatter.format(
      this.roundToDecimalPlaces(
        this.clamp(
          this.rewardTiers[this.highestUnlockedTier].priceThreshold - this.cartTotal,
          0,
          999,
        ),
        2,
      ),
    );
  }
}
